/**
 * New-session rate-limit detection for `POST /bot-agent/sessions`.
 *
 * The BE guards session creation with a per-IP quota (BotRateLimitService —
 * 20 new sessions/hour) and rejects with a 403 whose body message reads
 * `Too many new sessions. Retry after <n>s.`
 *
 * That SAME endpoint also 403s for the eligibility gate ("Bot agent not
 * available for this tenant/website"), which is a permanent condition and must
 * NOT put the widget into a countdown. So detection is deliberately narrow:
 * we only claim a cooldown when the response actually looks like the quota
 * rejection, and fall through to generic error handling otherwise.
 */

/** Mirrors BotRateLimitService.ipSessionWindowSec — used only when the BE tells
 *  us we're limited but not for how long (older builds send no seconds). */
export const DEFAULT_NEW_SESSION_RETRY_SEC = 3600;

/** Clamp so a malformed/hostile value can't disable the widget for days. */
const MAX_RETRY_SEC = 24 * 3600;

/** Thrown by `createSession` so callers can react to the quota specifically
 *  instead of pattern-matching HTTP details at the call site. */
export class BotNewSessionLimitError extends Error {
  /** Seconds until a new session may be created, as reported by the BE. */
  readonly retryAfterSec: number;
  /** Absolute epoch-ms deadline. Stored/compared instead of the raw seconds so
   *  the countdown stays correct across reloads and tab sleeps. */
  readonly retryAt: number;

  constructor(retryAfterSec: number) {
    super(`Bot new-session quota reached; retry in ${retryAfterSec}s`);
    this.name = 'BotNewSessionLimitError';
    this.retryAfterSec = retryAfterSec;
    this.retryAt = Date.now() + retryAfterSec * 1000;
  }
}

function clampSeconds(value: number): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.min(Math.ceil(value), MAX_RETRY_SEC);
}

/**
 * Pull the retry window out of an axios-shaped error, preferring a real
 * `Retry-After` header (seconds) and falling back to the seconds embedded in
 * the message text, which is all the currently deployed BE sends.
 */
function extractRetrySec(error: any): number | null {
  const headers = error?.response?.headers ?? {};
  const header = headers['retry-after'] ?? headers['Retry-After'];
  if (header !== undefined && header !== null && header !== '') {
    const fromHeader = clampSeconds(Number(header));
    if (fromHeader !== null) return fromHeader;
  }

  const message = readMessage(error);
  const match = /retry\s+after\s+(\d+)\s*s/i.exec(message);
  if (match) return clampSeconds(Number(match[1]));

  return null;
}

/** The BE message can arrive as a string or as Nest's `{ message: [...] }`. */
function readMessage(error: any): string {
  const raw = error?.response?.data?.message ?? error?.response?.data?.error ?? '';
  return Array.isArray(raw) ? raw.join(' ') : String(raw);
}

/**
 * Returns a `BotNewSessionLimitError` when the failure is the new-session quota,
 * or `null` for every other failure (including the eligibility 403, network
 * errors, and 5xx) so the caller can fall back to generic handling.
 */
export function asNewSessionLimitError(error: unknown): BotNewSessionLimitError | null {
  const err = error as any;
  if (err?.response?.status !== 403) return null;

  const message = readMessage(err);
  const looksLikeQuota =
    /too many new sessions/i.test(message) || /retry\s+after/i.test(message);
  if (!looksLikeQuota) return null;

  return new BotNewSessionLimitError(
    extractRetrySec(err) ?? DEFAULT_NEW_SESSION_RETRY_SEC,
  );
}

/**
 * `mm:ss`, widening to `h:mm:ss` past an hour. Returns `0:00` at/below zero so
 * the label never renders as a negative or empty string mid-tick.
 */
export function formatCooldown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}
